// ─── Day mappings ────────────────────────────────────────────────────────────

// Keys are JS day index: 0 = Sunday, 1 = Monday, ..., 6 = Saturday
export const DAY_DISPLAY = {
  0: { vn: "Chủ Nhật", en: "Sunday" },
  1: { vn: "Thứ 2", en: "Monday" },
  2: { vn: "Thứ 3", en: "Tuesday" },
  3: { vn: "Thứ 4", en: "Wednesday" },
  4: { vn: "Thứ 5", en: "Thursday" },
  5: { vn: "Thứ 6", en: "Friday" },
  6: { vn: "Thứ 7", en: "Saturday" },
};

export const getTodayDay = () => new Date().getDay();

export const getActiveDay = () => {
  const params = new URLSearchParams(window.location.search);
  const dayParam = params.get("day");
  const today = dayParam !== null ? parseInt(dayParam, 10) : getTodayDay();

  return today;
};

export const getMenuText = (language) =>
  language === "en" ? TEXT_EN : TEXT_VN;

export const getDisplayDay = (activeDay, language) =>
  DAY_DISPLAY[activeDay]?.[language] || activeDay;

export const getMenuHeading = (activeDay, language) => {
  const displayDay = getDisplayDay(activeDay, language);
  return language === "en"
    ? `::${displayDay || ""}:: Menu`
    : `Thực đơn ::${displayDay || ""}`;
};

// ─── Category mappings ────────────────────────────────────────────────────────

export const CATEGORY_DISPLAY = {
  featured: {
    gg_key: "mua-nao-thuc-ay",
    vn: "Mùa nào thức ấy",
    en: "Seasonal Flavors",
  },
  protein: { gg_key: "dam", vn: "Đạm", en: "Main Dishes" },
  vegetable: {
    gg_key: "rau-va-canh",
    vn: "Rau và canh",
    en: "Vegetables & Soups",
  },
  rice: { gg_key: "com-va-an-kem", vn: "Cơm và ăn kèm", en: "Rice & Sides" },
  hotpot: { gg_key: "lau", vn: "Lẩu ngon Xới Cơm", en: "Savory Hotpots" },
  drinks: { gg_key: "giai-khat", vn: "Giải khát", en: "Drinks" },
  dessert: {
    gg_key: "trang-mieng",
    vn: "Tráng miệng nhà làm",
    en: "Homemade Desserts",
  },
};

export const getCategoryDisplay = (category, language) =>
  CATEGORY_DISPLAY[category]?.[language] || category;

// Normalize any string to a slug (remove diacritics, lowercase, spaces → hyphens)
const toSlug = (str) => {
  if (!str) return "";

  const strNoAccent = str
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[đĐ]/g, (c) => (c === "đ" ? "d" : "D"));
  const stringLowerCase = strNoAccent.toLowerCase().trim();
  const slug = stringLowerCase.replace(/\s+/g, "-");

  return slug;
};

// Map gg_key slug → category key
const getCategoryArrWithGGKeys = (categoriesArrayWithKeys) => {
  const categoriesArrayWithGGKeys = categoriesArrayWithKeys.map(
    ([key, { gg_key }]) => [gg_key, key],
  );
  return categoriesArrayWithGGKeys;
};
const CATERGORY_ARR = Object.entries(CATEGORY_DISPLAY);
const CATEGORY_GG_KEY_TO_KEY = Object.fromEntries(
  getCategoryArrWithGGKeys(CATERGORY_ARR),
);

export const FEATURED_CATEGORY = "featured";
export const LEFT_CATEGORIES = ["protein", "vegetable", "rice"];
export const RIGHT_CATEGORIES = ["drinks", "dessert", "hotpot"];

export const getColumnEntries = (categories, grouped) => {
  const categoryEntriesFiltered = categories.filter((cat) => grouped[cat]);
  const categoryEntries = categoryEntriesFiltered.map((cat) => [
    cat,
    grouped[cat],
  ]);
  return categoryEntries;
};

// ─── Parsing ──────────────────────────────────────────────────────────────────

const formatPrice = (raw) => {
  if (!raw && raw !== 0) return "";
  if (typeof raw === "number") {
    // 60000 → "60.000đ"
    return raw.toLocaleString("vi-VN") + "đ";
  }
  return String(raw);
};

const getMenuItem = (menuRow) => {
  const [, , categoryRaw, showOnMenu, code, nameVn, nameEn, price] = menuRow;
  // showOnMenu is a boolean (Apps Script returns raw cell values, not "TRUE"/"FALSE" strings)
  const trimmedNameVn = nameVn ? nameVn.trim() : "";
  const trimedNameEn = nameEn ? nameEn.trim() : "";
  if (showOnMenu !== true || !trimmedNameVn || trimmedNameVn === "#N/A") return;
  const category =
    CATEGORY_GG_KEY_TO_KEY[toSlug(categoryRaw)] || toSlug(categoryRaw);

  return {
    category,
    code: code || "",
    nameVn: trimmedNameVn,
    nameEn: trimedNameEn || trimmedNameVn,
    price: formatPrice(price),
  };
};

// Row format: [STT, Ngày, Category, ShowOnMenu, Code, NameVn, NameEn, Price]
// rows[0] may be an announcement row (blank Category) or a menu item (known Category).
// Detection uses Category (position 2) against the known map — deploy order doesn't matter.
export const parseMenuRows = (rows) => {
  if (!rows || rows.length === 0) return { items: [], announcement: { vn: "", en: "" } };

  const [firstRow = [], ...restRows] = rows;

  const categoryRaw = firstRow[2];
  const isAnnouncementRow =
    !categoryRaw || !CATEGORY_GG_KEY_TO_KEY[toSlug(categoryRaw)];

  const announcement = isAnnouncementRow
    ? {
        vn: typeof firstRow[5] === "string" ? firstRow[5].trim() : "",
        en: typeof firstRow[6] === "string" ? firstRow[6].trim() : "",
      }
    : { vn: "", en: "" };

  const itemRows = isAnnouncementRow ? restRows : rows;

  let items = [];
  itemRows.forEach((row) => {
    const newItem = getMenuItem(row);
    if (!newItem) return;
    items = [...items, newItem];
  });

  return { items, announcement };
};

export const groupByCategory = (items) => {
  const itemsByCategory = items.reduce((acc, item) => {
    const { category } = item;

    if (!acc[category]) acc[category] = [];

    acc[category].push(item);
    return acc;
  }, {});

  return itemsByCategory;
};

export const toMenuEntry = (items, image) => ({ items, image: image || "" });

export const attachCategoryImages = (grouped) => {
  const groupedArray = Object.entries(grouped);
  const groupedArrayWithImage = groupedArray.map(([category, items]) => [
    category,
    toMenuEntry(items, CATEGORY_IMAGE_MAP[category]),
  ]);

  const groupedObjWithImage = Object.fromEntries(groupedArrayWithImage);
  return groupedObjWithImage;
};

// ─── Images ───────────────────────────────────────────────────────────────────

export const MENU_IMAGES = {
  FEATURED: "/assets/Menu/menuFeature.svg",
  PLATE: "/assets/Menu/plate.svg",
  RICE: "/assets/Menu/com.svg",
  SOUP: "/assets/Menu/canh.svg",
};

export const CATEGORY_IMAGE_MAP = {
  protein: MENU_IMAGES.PLATE,
  vegetable: MENU_IMAGES.SOUP,
  rice: MENU_IMAGES.RICE,
  hotpot: "",
  drinks: "",
  dessert: "",
};

// ─── Sheet ranges (English keys) ─────────────────────────────────────────────

// Column ranges per day — passed as ?range= to the Apps Script endpoint
// Keys are JS day index (matches getTodayDay / new Date().getDay())
export const MENU_RANGES = {
  1: "B3:I201", // Monday
  2: "J3:Q201", // Tuesday
  3: "R3:Y201", // Wednesday
  4: "Z3:AG201", // Thursday
  5: "AH3:AO201", // Friday
  6: "AP3:AW201", // Saturday
  0: "AX3:BE201", // Sunday
};

// ─── Static UI text ───────────────────────────────────────────────────────────

export const TEXT_VN = {
  subtitle:
    "Chúng tôi cung cấp thực đơn đổi món hàng ngày, dựa trên sự tươi mới của nguyên liệu trong phiên chợ sớm.",
  featuredHeading: "Mùa nào thức ấy",
  notesHeading: "Lưu ý trong thực đơn:",
  notes: [
    "Chúng tôi cam kết dùng nguyên liệu đặc sản của Việt Nam, không phụ thuộc vào các nguyên liệu nhập khẩu.",
    "Do đó, thực đơn của chúng tôi linh hoạt và thích ứng với sự thay đổi theo mùa của miền Bắc Việt Nam, đảm bảo mang đến cho bạn trải nghiệm ẩm thực đặc trưng và độc đáo.",
    "Nếu có bất kỳ yêu cầu đặc biệt về đồ ăn (hoặc thông tin dị ứng), vui lòng thông báo cho chúng tôi ít nhất 24 giờ trước khi dùng bữa.",
  ],
  error: "Không thể tải thực đơn. Vui lòng thử lại sau.",
  empty: "Chưa có thực đơn cho ngày này.",
};

export const TEXT_EN = {
  subtitle:
    "We offer a daily rotating menu based on the freshest ingredients from the early morning market.",
  featuredHeading: "Seasonal Flavors",
  notesHeading: "Menu Notes:",
  notes: [
    "Our current menu may not accommodate all food allergies. However, if you have any special dietary requirements, please notify us at least 48 hours in advance.",
    "We are committed to using authentic Vietnamese specialties and do not rely on imported ingredients.",
    "As a result, our menu is flexible and adapts to the changing seasons of Northern Vietnam, ensuring a unique and characteristic culinary experience.",
    "If you have any specific dietary requirements or allergy information, please notify us at least 24 hours before your meal.",
  ],
  error: "Unable to load menu. Please try again later.",
  empty: "No menu available for today.",
};
